export const PRICING_V2_FLAG = 'pricing_v2';

export const isPricingV2Enabled = (organization) =>
  organization?.publicMetadata?.[PRICING_V2_FLAG] === true;

export const FEATURES = Object.freeze({
  playlistGeneration: 'playlist_generation',
  automatedSync: 'automated_sync',
  stations3: 'stations_3',
  stationsUnlimited: 'stations_unlimited',
  autoSyncUnlimited: 'autosync_unlimited',
  prioritySupport: 'priority_support',
  earlyAccess: 'early_access',
});

export const BASE_STATION_LIMIT = 1;
export const STARTER_AUTO_SYNC_DESIGN_LIMIT = 3;
export const AUTO_SYNC_DESIGNS_KEY = 'auto_sync_designs';

export const PLAN_CATALOG = Object.freeze([
  Object.freeze({
    slug: 'starter',
    name: 'Starter',
    listPriceUsd: 49,
    features: Object.freeze([FEATURES.playlistGeneration, FEATURES.automatedSync]),
  }),
  Object.freeze({
    slug: 'turbo',
    name: 'Turbo',
    listPriceUsd: 99,
    features: Object.freeze([
      FEATURES.playlistGeneration,
      FEATURES.automatedSync,
      FEATURES.stations3,
      FEATURES.autoSyncUnlimited,
    ]),
  }),
  Object.freeze({
    slug: 'max',
    name: 'Max',
    listPriceUsd: 149,
    features: Object.freeze([
      FEATURES.playlistGeneration,
      FEATURES.automatedSync,
      FEATURES.stationsUnlimited,
      FEATURES.autoSyncUnlimited,
      FEATURES.prioritySupport,
      FEATURES.earlyAccess,
    ]),
  }),
]);

export const catalogPlan = (slug) => PLAN_CATALOG.find((plan) => plan.slug === slug) ?? null;

export const INTRO_PROMO = Object.freeze({
  code: 'TURBOINTRO',
  amountOffUsd: 20,
  redeemableThrough: '2026-12-31',
  duration: Object.freeze({ kind: 'forever' }),
});

export const FOUNDING_PROMO = Object.freeze({
  code: 'FOUNDING',
  amountOffUsd: 60,
  plans: Object.freeze(['turbo']),
  duration: Object.freeze({ kind: 'forever' }),
  maxRedemptions: 10,
});

const endOfDayUtc = (isoDate) => Date.parse(`${isoDate}T23:59:59.999Z`);

export const isPromoRedeemable = (promo, now = new Date()) =>
  now.getTime() <= endOfDayUtc(promo.redeemableThrough);

export const promoDurationLabel = ({ duration }) => {
  if (duration.kind === 'forever') return 'for the life of your subscription';
  if (duration.kind === 'months') {
    return duration.months === 1 ? 'for your first month' : `for your first ${duration.months} months`;
  }
  throw new Error(`Unknown promo duration kind: ${duration.kind}`);
};

export const introPricing = (plan, promo = INTRO_PROMO, now = new Date()) => {
  if (!isPromoRedeemable(promo, now)) {
    return { listPriceUsd: plan.listPriceUsd, priceUsd: plan.listPriceUsd, promo: null };
  }
  return {
    listPriceUsd: plan.listPriceUsd,
    priceUsd: Math.max(0, plan.listPriceUsd - promo.amountOffUsd),
    promo: {
      code: promo.code,
      note: `Use ${promo.code} at checkout`,
      terms: `$${promo.amountOffUsd} off ${promoDurationLabel(promo)}. Redeem by ${promo.redeemableThrough}.`,
    },
  };
};

export const entitlementsFromFeatures = (featureSlugs) => {
  const has = (slug) => featureSlugs.includes(slug);
  let maxStations = BASE_STATION_LIMIT;
  if (has(FEATURES.stations3)) maxStations = 3;
  if (has(FEATURES.stationsUnlimited)) maxStations = null;
  const automatedSync = has(FEATURES.automatedSync);
  return {
    maxStations,
    automatedSync,
    autoSyncDesignLimit: automatedSync && !has(FEATURES.autoSyncUnlimited) ? STARTER_AUTO_SYNC_DESIGN_LIMIT : null,
    playlistGeneration: has(FEATURES.playlistGeneration),
    prioritySupport: has(FEATURES.prioritySupport),
    earlyAccess: has(FEATURES.earlyAccess),
  };
};

export const planFromFeatures = (featureSlugs, planSlug = null) => {
  const known = catalogPlan(planSlug);
  return {
    slug: known?.slug ?? null,
    name: known?.name ?? 'No plan',
    listPriceUsd: known?.listPriceUsd ?? null,
    ...entitlementsFromFeatures(featureSlugs),
  };
};

const ALL_FEATURE_SLUGS = Object.values(FEATURES);

export const planFromHas = (has) => {
  const featureSlugs = ALL_FEATURE_SLUGS.filter((feature) => has({ feature }));
  const planSlug = PLAN_CATALOG.find(({ slug }) => has({ plan: slug }))?.slug ?? null;
  return planFromFeatures(featureSlugs, planSlug);
};

const ACTIVE_ITEM_STATUSES = new Set(['active', 'past_due']);

export const featureSlugsFromSubscription = (subscription) => {
  const items = subscription?.subscription_items ?? subscription?.items ?? [];
  return [
    ...new Set(
      items
        .filter((item) => ACTIVE_ITEM_STATUSES.has(item?.status))
        .flatMap((item) =>
          Array.isArray(item?.plan?.features)
            ? item.plan.features.map((feature) => feature?.slug)
            : catalogPlan(item?.plan?.slug)?.features ?? [],
        )
        .filter((slug) => typeof slug === 'string'),
    ),
  ];
};

export const canLinkStation = (plan, linkedCount) =>
  !plan || plan.maxStations === null || linkedCount < plan.maxStations;

export const enrolledAutoSyncDesigns = (organization) => {
  const ids = organization?.publicMetadata?.[AUTO_SYNC_DESIGNS_KEY];
  return Array.isArray(ids) ? ids.filter((id) => typeof id === 'string' && id.length > 0) : [];
};

export const autoSyncScope = (plan, enrolledIds) => {
  if (!plan) return null;
  if (!plan.automatedSync) return [];
  if (plan.autoSyncDesignLimit === null) return null;
  return enrolledIds.slice(0, plan.autoSyncDesignLimit);
};

const nextPlanFor = (plan, satisfies) => {
  const current = plan.listPriceUsd ?? 0;
  return PLAN_CATALOG.find(
    (candidate) => candidate.listPriceUsd > current && satisfies(entitlementsFromFeatures(candidate.features)),
  ) ?? null;
};

const stationCapacity = (maxStations) => maxStations ?? Infinity;

const stationLabel = (maxStations) => {
  if (maxStations === null) return 'unlimited Media Stations';
  return maxStations === 1 ? '1 Media Station' : `up to ${maxStations} Media Stations`;
};

const capitalize = (text) => `${text.charAt(0).toUpperCase()}${text.slice(1)}`;

const planLabel = (plan) => (plan.slug ? `Your ${plan.name} plan` : 'Without a plan, your organization');

export const stationLimitMessage = (plan) => {
  const next = nextPlanFor(plan, (e) => stationCapacity(e.maxStations) > stationCapacity(plan.maxStations));
  const base = `${planLabel(plan)} includes ${stationLabel(plan.maxStations)}.`;
  return next ? `${base} Upgrade to ${next.name} to link ${stationLabel(entitlementsFromFeatures(next.features).maxStations)}.` : base;
};

export const automatedSyncMessage = (plan) => {
  const next = nextPlanFor(plan, (e) => e.automatedSync);
  return `Auto-sync needs a plan. Choose ${next.name} or higher to turn it on.`;
};

export const autoSyncDesignLimitMessage = (plan) => {
  const next = nextPlanFor(plan, (e) => e.automatedSync && e.autoSyncDesignLimit === null);
  const base = `${planLabel(plan)} auto-syncs up to ${plan.autoSyncDesignLimit} designs.`;
  return next ? `${base} Upgrade to ${next.name} for unlimited auto-sync.` : base;
};

export const linkStationGate = (plan, linkedCount) =>
  canLinkStation(plan, linkedCount)
    ? { allowed: true, message: null }
    : { allowed: false, message: stationLimitMessage(plan) };

export const autoSyncGate = (plan, currentlyEnabled) =>
  !plan || currentlyEnabled || plan.automatedSync
    ? { canTurnOn: true, message: null }
    : { canTurnOn: false, message: automatedSyncMessage(plan) };

export const autoSyncEnrollmentGate = (plan, enrolledIds, designId) => {
  if (!plan || enrolledIds.includes(designId)) return { allowed: true, message: null };
  if (!plan.automatedSync) return { allowed: false, message: automatedSyncMessage(plan) };
  if (plan.autoSyncDesignLimit === null || enrolledIds.length < plan.autoSyncDesignLimit) {
    return { allowed: true, message: null };
  }
  return { allowed: false, message: autoSyncDesignLimitMessage(plan) };
};

export const planErrorMessage = (error, plan) => {
  if (!plan) return null;
  if (error?.code === 'station_limit_reached') return stationLimitMessage(plan);
  if (error?.code === 'automated_sync_not_in_plan') return automatedSyncMessage(plan);
  if (error?.code === 'autosync_design_limit_reached') return autoSyncDesignLimitMessage(plan);
  return null;
};

export const planSummary = (plan) => ({
  name: plan.slug ? `${plan.name} plan` : 'No plan yet',
  features: [
    capitalize(stationLabel(plan.maxStations)),
    ...(plan.playlistGeneration ? ['Playlist generation'] : []),
    plan.automatedSync
      ? plan.autoSyncDesignLimit === null
        ? 'Unlimited auto-sync designs'
        : `Auto-sync for up to ${plan.autoSyncDesignLimit} designs`
      : 'No auto-sync',
    ...(plan.prioritySupport ? ['Priority support'] : []),
    ...(plan.earlyAccess ? ['Early access to new features and releases'] : []),
  ],
});
